import { useCallback, useEffect, useRef, useState } from 'react';

import { GetUserRightsResponse, UserRight } from '@amzn/coral_com-amazon-asxcustomerprofileservice';
import { useLogger } from '@amzn/sss-website-theme';

import { useAcpsCreditsClient } from 'src/clients/acps';

export interface UserRightsContext {
  userRights: UserRight[];
  isAuthenticated: boolean;
  isLoading: boolean;
  error: any;
}

export const SESSION_STORAGE_KEY = 'userRights';
const POLL_INTERVAL_MS = 100;
const POLL_TIMEOUT_MS = 10000;

/**
 * Reads user rights from session storage. The trim (SSSAshTrimWebsite) is responsible
 * for fetching user rights from ACPS and caching them in session storage.
 *
 * @returns parsed user rights array, or null if not yet available in session storage
 */
const getUserRightsFromSessionStorage = (): string[] | null => {
  try {
    const userRightsFromSession = sessionStorage.getItem(SESSION_STORAGE_KEY);
    if (userRightsFromSession !== null) {
      return userRightsFromSession
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean);
    }
  } catch {
    // sessionStorage may be unavailable in some contexts (e.g., SSR, restricted iframe)
  }
  return null;
};

/**
 * Custom hook to retrieve user rights from session storage, with an API fallback.
 *
 * User rights are fetched and cached in session storage by the trim (SSSAshTrimWebsite),
 * which serves as the single source of truth for user rights. This hook reads from that
 * cache rather than making its own API calls to ACPS.
 *
 * If session storage is not yet populated (e.g., trim hasn't completed its fetch),
 * the hook will poll briefly until the data becomes available. On a cache miss (polling
 * timeout), it falls back to fetching user rights directly from ACPS and caches the
 * result in session storage.
 *
 * @returns {UserRightsContext} Object containing user rights, authentication status, loading state, and any errors
 */
export const useGetUserRights = (): UserRightsContext => {
  const logger = useLogger();
  const { getUserRights } = useAcpsCreditsClient();

  const initialRights = getUserRightsFromSessionStorage();

  const [userRights, setUserRights] = useState<UserRight[]>(() => (initialRights as UserRight[]) || []);
  const [isLoading, setIsLoading] = useState<boolean>(() => initialRights === null);
  const isMountedRef = useRef(true);
  const hasResolvedRef = useRef(initialRights !== null);

  useEffect(
    () => () => {
      isMountedRef.current = false;
    },
    []
  );

  const fetchAndCacheUserRights = useCallback(async () => {
    try {
      const response: GetUserRightsResponse = await getUserRights();
      if (!isMountedRef.current) return;
      const rights = (response?.userRights as UserRight[]) || [];
      setUserRights(rights);
      try {
        sessionStorage.setItem(SESSION_STORAGE_KEY, rights.join(','));
      } catch {
        // sessionStorage may be unavailable (e.g., quota exceeded, restricted iframe).
        // Rights are already set in React state via setUserRights above, so the hook
        // will return correct values for the current component lifetime. Only the
        // session storage cache is missing, meaning future remounts will re-fetch.
      }
      setIsLoading(false);
    } catch (err) {
      if (!isMountedRef.current) return;
      setIsLoading(false);
      logger.logError('useGetUserRights', 'Failed to load user rights from API.', err);
    }
    // getUserRights is stable (memoized in AcpsCreditsClientProvider via useMemo with empty deps).
    // If that stability contract changes, add it back to deps.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getUserRights]);

  useEffect(() => {
    // If already resolved on mount (session storage was available), skip polling
    if (hasResolvedRef.current) {
      return () => {};
    }

    // Poll for session storage to be populated by the trim
    let timeout: NodeJS.Timeout;
    let cleared = false;

    const interval = setInterval(() => {
      if (cleared || !isMountedRef.current) return;
      const rights = getUserRightsFromSessionStorage();
      if (rights !== null) {
        hasResolvedRef.current = true;
        setUserRights(rights as UserRight[]);
        setIsLoading(false);
        clearInterval(interval);
        clearTimeout(timeout);
        cleared = true;
      }
    }, POLL_INTERVAL_MS);

    timeout = setTimeout(() => {
      if (cleared || !isMountedRef.current) return;
      cleared = true;
      clearInterval(interval);
      logger.logError(
        'useGetUserRights',
        'Timed out waiting for user rights from session storage. Falling back to API fetch.'
      );
      fetchAndCacheUserRights();
    }, POLL_TIMEOUT_MS);

    return () => {
      cleared = true;
      clearInterval(interval);
      clearTimeout(timeout);
    };
    // Dependencies intentionally empty — polling runs once on mount.
    // logger and fetchAndCacheUserRights are stable refs (useCallback with stable deps).
    // If their stability contract changes, add them back to deps.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isAuthenticated = userRights.includes(UserRight.AUTHENTICATED);

  return {
    userRights,
    isAuthenticated,
    isLoading,
    error: null,
  };
};
